import type { SegmentationResult } from '../asset/image-segmentation-service';
import type { PartImageRegionBox } from './part-image-observation';

const MOSTLY_COVERED_SHARE = 0.5;

const MIN_SNAP_OVERLAP = 0.3;
const MAX_SNAP_GROWTH = 0.25;

export const PART_IMAGE_SEGMENTATION_OPTIONS = { pointsPerSide: 32, sensitivity: 50 };

const UNSEGMENTED = 0;

interface PixelBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface PartImageSegments {
  labels: Uint16Array;
  width: number;
  height: number;
  pixelCounts: Uint32Array;
  bounds: PixelBounds[];
}

function decodeLabels(regionMap: string): Uint16Array {
  const binary = atob(regionMap);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.codePointAt(index) ?? 0;
  const view = new DataView(bytes.buffer);
  return Uint16Array.from({ length: bytes.length / 2 }, (_, index) => view.getUint16(index * 2, true));
}

const emptyBounds = (): PixelBounds => ({ minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

function measureSegments(labels: Uint16Array, width: number): Pick<PartImageSegments, 'pixelCounts' | 'bounds'> {
  const labelCount = labels.reduce((max, label) => Math.max(max, label), 0) + 1;
  const pixelCounts = new Uint32Array(labelCount);
  const bounds = Array.from({ length: labelCount }, emptyBounds);
  for (const [index, label] of labels.entries()) {
    const x = index % width;
    const y = Math.floor(index / width);
    const segment = bounds[label];
    pixelCounts[label] += 1;
    segment.minX = Math.min(segment.minX, x);
    segment.minY = Math.min(segment.minY, y);
    segment.maxX = Math.max(segment.maxX, x);
    segment.maxY = Math.max(segment.maxY, y);
  }
  return { pixelCounts, bounds };
}

export function segmentsFromLabels(labels: Uint16Array, width: number, height: number): PartImageSegments {
  return { labels, width, height, ...measureSegments(labels, width) };
}

export function indexSegments(result: SegmentationResult): PartImageSegments {
  return segmentsFromLabels(decodeLabels(result.regionMap), result.width, result.height);
}

const clampToRange = (value: number, max: number): number => Math.min(Math.max(value, 0), max);

function toPixelBounds(box: PartImageRegionBox, segments: PartImageSegments): PixelBounds {
  return {
    minX: clampToRange(Math.floor(box.left * segments.width), segments.width - 1),
    minY: clampToRange(Math.floor(box.top * segments.height), segments.height - 1),
    maxX: clampToRange(Math.ceil((box.left + box.width) * segments.width) - 1, segments.width - 1),
    maxY: clampToRange(Math.ceil((box.top + box.height) * segments.height) - 1, segments.height - 1),
  };
}

function countPixelsInside(area: PixelBounds, segments: PartImageSegments): Uint32Array {
  const inside = new Uint32Array(segments.pixelCounts.length);
  for (let y = area.minY; y <= area.maxY; y += 1) {
    const row = y * segments.width;
    for (let x = area.minX; x <= area.maxX; x += 1) inside[segments.labels[row + x]] += 1;
  }
  return inside;
}

function coveredSegments(box: PartImageRegionBox, segments: PartImageSegments): number[] {
  const inside = countPixelsInside(toPixelBounds(box, segments), segments);
  const covered: number[] = [];
  for (const [label, count] of inside.entries()) {
    if (label !== UNSEGMENTED && count > 0 && count >= segments.pixelCounts[label] * MOSTLY_COVERED_SHARE) {
      covered.push(label);
    }
  }
  return covered;
}

export function outlineOfSegments(labels: number[], segments: PartImageSegments): PartImageRegionBox {
  const union = labels.reduce((combined, label) => {
    const segment = segments.bounds[label];
    return {
      minX: Math.min(combined.minX, segment.minX),
      minY: Math.min(combined.minY, segment.minY),
      maxX: Math.max(combined.maxX, segment.maxX),
      maxY: Math.max(combined.maxY, segment.maxY),
    };
  }, emptyBounds());
  return {
    left: union.minX / segments.width,
    top: union.minY / segments.height,
    width: (union.maxX + 1 - union.minX) / segments.width,
    height: (union.maxY + 1 - union.minY) / segments.height,
  };
}

const boxRight = (box: PartImageRegionBox): number => box.left + box.width;
const boxBottom = (box: PartImageRegionBox): number => box.top + box.height;

function overlapRatio(first: PartImageRegionBox, second: PartImageRegionBox): number {
  const width = Math.max(0, Math.min(boxRight(first), boxRight(second)) - Math.max(first.left, second.left));
  const height = Math.max(0, Math.min(boxBottom(first), boxBottom(second)) - Math.max(first.top, second.top));
  const shared = width * height;
  return shared / (first.width * first.height + second.width * second.height - shared);
}

function withinGrowth(snapped: PartImageRegionBox, box: PartImageRegionBox): PartImageRegionBox {
  const left = Math.max(snapped.left, box.left - MAX_SNAP_GROWTH * box.width);
  const top = Math.max(snapped.top, box.top - MAX_SNAP_GROWTH * box.height);
  const right = Math.min(boxRight(snapped), boxRight(box) + MAX_SNAP_GROWTH * box.width);
  const bottom = Math.min(boxBottom(snapped), boxBottom(box) + MAX_SNAP_GROWTH * box.height);
  return { left, top, width: right - left, height: bottom - top };
}

export function snapBoxToSegments(box: PartImageRegionBox, segments: PartImageSegments): PartImageRegionBox {
  const covered = coveredSegments(box, segments);
  if (covered.length === 0) return box;
  const snapped = outlineOfSegments(covered, segments);
  return overlapRatio(box, snapped) >= MIN_SNAP_OVERLAP ? withinGrowth(snapped, box) : box;
}
